import axios from "axios";
import { CubeRawRow, initCubeInquiry, queryChartType, QueryChartTypeKey } from "../schemas/kpi-props.schema";

import { routes } from "../../../vam/General";
import {
    buildBaseFilters,
    buildBaseFiltersTableSummary,
    groupByAccident,
    mapCubeData,
} from "../schemas/dashboard.utils";
import {
    ConsumptionTable,
    CubeBasicFilters,
    CubeQuery,
    CubeSiniestroRow,
    CubeSummaryResponse,
} from "../schemas/kpi.types";

interface CubeApiResponse<T> {
    data: T[];
}

const statsURL = `${routes.api}/health-disaster/chart`;

const postCube = async <T>(url: string, cubeBody: CubeQuery): Promise<T[]> => {
    try {
        const response = await axios.post<CubeApiResponse<T>>(url, cubeBody);
        return response.data.data ?? [];
    } catch (error) {
        console.error("Cube API error:", error);
        return [];
    }
};

export const kpiService = {
    fetchAccident: async (cubeFilter: CubeBasicFilters, quotesExist: boolean): Promise<ConsumptionTable[]> => {
        let query: CubeQuery;

        if (quotesExist) {
            query = {
                measures: [
                    "siniestros_salud_mensualizado.total_primas",
                    "siniestros_salud_mensualizado.reclamos_incurridos",
                    "siniestros_salud_mensualizado.siniestralidad",
                ],
                dimensions: [
                    "siniestros_salud_mensualizado.periodo",
                    "siniestros_salud_mensualizado.fecha_desde_portal",
                ],
                filters: buildBaseFiltersTableSummary(cubeFilter),
                order: {
                    "siniestros_salud_mensualizado.fecha_desde_portal": "asc",
                },
            };
        } else {
            query = {
                measures: ["siniestros_salud.reclamos_incurridos", "siniestros_salud.siniestralidad"],
                dimensions: ["siniestros_salud.primas_facturadas"],
                filters: buildBaseFilters(cubeFilter),
                timeDimensions: [
                    {
                        dimension: "siniestros_salud.fecha_pago",
                        granularity: "month",
                    },
                ],
            };
        }

        const response = await axios.post<{ data: CubeSiniestroRow[] }>(statsURL, query);

        const data = response.data.data ?? [];

        if (quotesExist) {
            return data.map(item => ({
                paymentDate: item["siniestros_salud_mensualizado.periodo"],
                premiumsBilled: Number(item["siniestros_salud_mensualizado.total_primas"] || 0),
                premiumsIncurred: Number(item["siniestros_salud_mensualizado.reclamos_incurridos"] || 0),
                percentage: Number(item["siniestros_salud_mensualizado.siniestralidad"] || 0),
            }));
        }

        return data.map(item => ({
            paymentDate: item["siniestros_salud.fecha_pago.month"],
            premiumsBilled: Number(item["siniestros_salud.primas_facturadas"] || 0),
            premiumsIncurred: Number(item["siniestros_salud.reclamos_incurridos"] || 0),
            percentage: Number(item["siniestros_salud.siniestralidad"] || 0),
        }));
    },

    fetchTypeOfCare: async (
        cubeFilter: CubeBasicFilters,
        typeInquiry: QueryChartTypeKey
    ): Promise<CubeSummaryResponse[]> => {
        const baseFilters = buildBaseFilters(cubeFilter);

        const configMap: Record<
            QueryChartTypeKey,
            {
                dimension: string;
                query: CubeQuery;
            }
        > = {
            [queryChartType.OUTPATIENT_VS_INPATIENT]: {
                dimension: "siniestros_salud.tipo_atencion",
                query: {
                    measures: ["siniestros_salud.total_pagado"],
                    dimensions: ["siniestros_salud.tipo_atencion"],
                    filters: baseFilters,
                },
            },

            [queryChartType.INPATIENT_BY_RELATIONSHIP]: {
                dimension: "siniestros_salud.parentesco",
                query: {
                    measures: ["siniestros_salud.total_pagado"],
                    dimensions: ["siniestros_salud.tipo_atencion", "siniestros_salud.parentesco"],
                    filters: [
                        ...baseFilters,
                        {
                            dimension: "siniestros_salud.tipo_atencion",
                            operator: "equals",
                            values: ["Hospitalario"],
                        },
                    ],
                },
            },

            [queryChartType.OUTPATIENT_BY_RELATIONSHIP]: {
                dimension: "siniestros_salud.parentesco",
                query: {
                    measures: ["siniestros_salud.total_pagado"],
                    dimensions: ["siniestros_salud.tipo_atencion", "siniestros_salud.parentesco"],
                    filters: [
                        ...baseFilters,
                        {
                            dimension: "siniestros_salud.tipo_atencion",
                            operator: "equals",
                            values: ["Ambulatorio"],
                        },
                    ],
                },
            },

            [queryChartType.SEVERITY_FREQUENCY]: {
                dimension: "siniestros_salud.severidad",
                query: {
                    measures: ["siniestros_salud.count", "siniestros_salud.total_pagado"],
                    dimensions: ["siniestros_salud.severidad"],
                    filters: baseFilters,
                },
            },

            [queryChartType.TOP_DIAGNOSES]: initCubeInquiry,
            [queryChartType.MOST_USED_OUTPATIENT_PROVIDERS]: initCubeInquiry,
            [queryChartType.MOST_USED_INPATIENT_PROVIDERS]: initCubeInquiry,
            [queryChartType.MOST_USED_PROVIDERS]: initCubeInquiry,
            [queryChartType.DIAGNOSES_BY_GENDER]: initCubeInquiry,
        };

        const config = configMap[typeInquiry];

        const cubeRaw = await postCube<CubeRawRow>(statsURL, config.query);

        return mapCubeData(cubeRaw, config.dimension);
    },

    fetchDiagnosis: async (cubeFilter: CubeBasicFilters): Promise<CubeSummaryResponse[]> => {
        const query: CubeQuery = {
            measures: ["siniestros_salud.total_pagado", "siniestros_salud.count"],
            dimensions: ["siniestros_salud.diagnostico"],
            filters: buildBaseFilters(cubeFilter),
        };

        const rawData = await postCube<CubeRawRow>(statsURL, query);
        const mapped = mapCubeData(rawData, "siniestros_salud.diagnostico");

        return groupByAccident(mapped, 4);
    },

    fetchSupplier: async (cubeFilter: CubeBasicFilters, typeAssistance: string): Promise<CubeSummaryResponse[]> => {
        const query: CubeQuery = {
            measures: ["siniestros_salud.total_pagado", "siniestros_salud.count"],
            dimensions: ["siniestros_salud.tipo_red", "siniestros_salud.tipo_atencion"],
            filters: [
                ...buildBaseFilters(cubeFilter),
                {
                    dimension: "siniestros_salud.tipo_atencion",
                    operator: "equals",
                    values: [typeAssistance],
                },
            ],
        };

        const rawData = await postCube<CubeRawRow>(statsURL, query);

        return mapCubeData(rawData, "siniestros_salud.tipo_red");
    },

    fetchSupplierSummary: async (cubeFilter: CubeBasicFilters): Promise<CubeSummaryResponse[]> => {
        const dimension = "siniestros_salud.proveedor";
        const query = {
            measures: ["siniestros_salud.total_pagado"],
            dimensions: [dimension],
            filters: buildBaseFilters(cubeFilter),
        };

        const rawData = await postCube<CubeRawRow>(statsURL, query);
        const mapped = mapCubeData(rawData, dimension);

        return groupByAccident(mapped, 4);
    },

    fetchGenderSummary: async (cubeFilter: CubeBasicFilters, gender: string): Promise<CubeSummaryResponse[]> => {
        const dimension = "siniestros_salud.diagnostico";
        const query: CubeQuery = {
            measures: ["siniestros_salud.total_pagado", "siniestros_salud.count"],
            dimensions: ["siniestros_salud.genero", "siniestros_salud.diagnostico"],
            filters: [
                ...buildBaseFilters(cubeFilter),
                {
                    dimension: "siniestros_salud.genero",
                    operator: "equals",
                    values: [gender],
                },
            ],
        };
        const rawData = await postCube<CubeRawRow>(statsURL, query);
        const mapped = mapCubeData(rawData, dimension);

        return groupByAccident(mapped, 4);
    },
};
